/**
 * Synchronous global mirror of the authenticated state.
 * Why: TanStack Router's `beforeLoad` runs synchronously and reads from the router
 * context, but React context updates are batched/async — meaning after a login() the
 * router still sees the *previous* context. So we duplicate the minimal auth state
 * here and update it imperatively from the AuthProvider's effects.
 *
 * Usage: only call `isAuthenticatedSync()` from route guards. Components should keep
 * using `useAuth()` for reactive reads.
 */

import { STORAGE_KEYS } from './constants';
import { leggiChiave, removeKey, writeKey } from './storage';

/**
 * Duplicato letterale di `CLAIM_PORTAL` (`shared/permissions/claims.ts`): importarlo qui
 * creerebbe un ciclo, perché questo modulo è la base che gli altri usano. Se cambia il
 * nome del marker, cambia in due punti — ed è il test a ricordarlo.
 */
const PORTAL_CLAIM = 'clientPortal';

interface AuthMirror {
  token: string | null;
  tenantId: string | null;
  /**
   * I claim funzionali della sessione. Servono fuori da React — per esempio a decidere
   * dove mandare l'utente subito dopo il login, quando il `decoded` catturato dalla
   * closure del componente è ancora quello di prima.
   */
  claims: readonly string[];
}

let mirror: AuthMirror = { token: null, tenantId: null, claims: [] };

export function setAuthMirror(next: AuthMirror): void {
  mirror = next;
}

/**
 * Usata SOLO dalle route guard (`beforeLoad`), che girano in modo sincrono al primo
 * caricamento — prima che l'AuthProvider abbia idratato il mirror in memoria.
 *
 * Su **hard refresh / deep-link** il mirror riparte vuoto: se ci fermassimo a
 * `mirror.token` la guardia rimbalzerebbe SEMPRE al /login anche con sessione valida.
 * Fallback: consideriamo la sessione ripristinabile se esiste un **refresh token
 * persistito** (l'access token è solo in memoria; l'AuthProvider lo rigenera al mount).
 * Se il refresh token è scaduto/invalido, il refresh fallisce → doLogout rimuove il
 * token e `router.invalidate()` ri-esegue la guardia, che allora porta al /login.
 */
export function isAuthenticatedSync(): boolean {
  if (mirror.token) return true;
  return Boolean(leggiChiave(STORAGE_KEYS.refreshToken));
}

export function getAuthMirror(): AuthMirror {
  return mirror;
}

/**
 * «Questa persona può entrare nel portale clienti»: ha il marker `clientPortal`.
 *
 * ⚠️ Non è la stessa domanda di «quale shell montare». Da **P8** (BE `v1.147.0`) una persona può
 * essere dipendente in un'azienda **e** cliente in un'altra, e il marker sta sul **ruolo** — cioè
 * sulla persona — mentre la modalità la decide il **prefisso della rotta**. Quindi il marker dice
 * che il portale esiste per lei, non che sia il suo unico posto.
 */
export function canEnterPortalSync(): boolean {
  if (mirror.claims.includes(PORTAL_CLAIM)) return true;
  return leggiChiave(STORAGE_KEYS.portalSession) === '1';
}

/**
 * «Questa persona è anche di casa nel gestionale»: ha almeno un'appartenenza (`UserTenants`), che è
 * ciò che il payload di login chiama `associatedTenants`.
 *
 * Per un utente **solo cliente** quella lista è **vuota per costruzione** — il BE lo dichiara: il
 * componente di cambio azienda «cammina su `UserTenants`, vuota per costruzione» per un account di
 * portale. È il discriminante che distingue un cliente puro da un ibrido, ed è un dato che arriva
 * nello stesso payload dei token, quindi disponibile prima di qualunque chiamata.
 */
export function hasStaffMembershipSync(): boolean {
  return leggiChiave(STORAGE_KEYS.staffMembership) === '1';
}

/**
 * «La shell da montare è quella del portale».
 *
 * Vive anche in `localStorage` e non solo nel mirror perché le route guard girano **sincrone** al
 * primo render: su un hard reload il mirror è vuoto e l'access token non è ancora stato rigenerato
 * dal refresh, quindi senza il valore persistito la shell interna farebbe in tempo a montarsi. Per
 * un utente-cliente montarla significa sparare una dozzina di letture che il server nega tutte —
 * configurazione tenant, dipendente collegato, notifiche, contatori — e vedere una schermata di
 * errori al posto del portale.
 *
 * ⚠️ **Il caso ibrido** (P8): chi ha il marker **e** un'appartenenza interna deve partire nel
 * **gestionale**, non nel portale. Prima questa funzione guardava solo il marker, quindi un ibrido
 * sarebbe stato spinto nel portale e la guardia di `_app` lo avrebbe rimbalzato fuori da ogni
 * pagina interna — cioè P8 esisteva sul server e il client lo annullava. Al portale ci si va con un
 * gesto esplicito (vedi `canEnterPortalSync`).
 *
 * Non è un permesso e non decide cosa si può fare: quello lo fa il server. Decide **quale shell
 * montare**, che è una scelta di navigazione.
 */
export function isPortalSessionSync(): boolean {
  return canEnterPortalSync() && !hasStaffMembershipSync();
}

/** `true` se questa persona è **entrambe le cose**: cliente di qualcuno e interna da qualche parte. */
export function isHybridSync(): boolean {
  return canEnterPortalSync() && hasStaffMembershipSync();
}

/**
 * Allinea le copie persistite a ciò che è appena arrivato dal login/refresh.
 *
 * `hasStaffMembership` viene da `associatedTenants` del payload: si passa esplicitamente invece di
 * dedurlo, perché è l'unico posto che quel dato lo ha già in mano.
 */
export function persistPortalSession(claims: readonly string[], hasStaffMembership = false): void {
  if (claims.includes(PORTAL_CLAIM)) writeKey(STORAGE_KEYS.portalSession, '1');
  else removeKey(STORAGE_KEYS.portalSession);
  if (hasStaffMembership) writeKey(STORAGE_KEYS.staffMembership, '1');
  else removeKey(STORAGE_KEYS.staffMembership);
}

/** Al logout la copia persistita va via, o il prossimo che entra da questo browser
 *  verrebbe mandato nel portale prima ancora di sapere chi è. */
export function clearPortalSession(): void {
  removeKey(STORAGE_KEYS.portalSession);
  removeKey(STORAGE_KEYS.staffMembership);
  removeKey(STORAGE_KEYS.claims);
}

/**
 * Salva i claim dell'ultima sessione, per disegnare il menu al primo render del reload.
 *
 * Perché serve: i claim non stanno nel JWT (arrivano nel `claimsList` della busta) e l'access
 * token vive solo in memoria. Su hard refresh, quindi, per il tempo del round-trip di refresh non
 * sappiamo che permessi ha chi guarda — e un menu che compare largo e poi si stringe mostra per un
 * istante proprio le voci che il gating esiste per nascondere.
 *
 * ⚠️ Vale per **disegnare**, non per autorizzare: chi decide è il server. La copia è per-tenant e
 * viene riscritta da ogni login, refresh e cambio azienda, che passano tutti da `applyAuthResponse`.
 */
export function persistClaims(claims: readonly string[]): void {
  if (claims.length) writeKey(STORAGE_KEYS.claims, claims.join(','));
  else removeKey(STORAGE_KEYS.claims);
}

/**
 * I claim dell'ultima sessione conosciuta.
 *
 * ⚠️ Possono essere **vecchi**: se un super-admin cambia i ruoli di questa persona, questa copia
 * resta quella di prima finché il refresh non risponde — esattamente come il token, che porta i
 * vecchi claim fino alla scadenza (manuale funzionale §1.5). Per questo serve a disegnare e non a
 * decidere.
 */
export function loadPersistedClaims(): string[] {
  const raw = leggiChiave(STORAGE_KEYS.claims);
  if (!raw) return [];
  return raw.split(',').map((c) => c.trim()).filter(Boolean);
}
